import React from "react";
import { useForm } from "react-hook-form";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { FormHelpCallout } from "../FormHelpCallout";
import { DateInputWithShortcut } from "../DateInputWithShortcut";
import { toDateInputValue } from "../../../utils/date";
import type { CreateExpenseData } from "../../../services/api/financial.service";

export interface ExpenseFormProps {
  onSubmit: (data: CreateExpenseData) => void;
  onCancel: () => void;
  onDelete?: () => void;
  onRevise?: () => void;
  canRevise?: boolean;
  initialData?: Partial<CreateExpenseData>;
  isLoading?: boolean;
  isEditing?: boolean;
  startupStartDate?: Date | string | null;
}

const EXPENSE_CATEGORIES = [
  "Salary",
  "Marketing",
  "Operations",
  "Product",
  "Legal",
  "Other",
];

const EXPENSE_COLORS = [
  "#b893a6", // mauve rose
  "#9b8ec4", // violet
  "#7c9fd4", // slate blue
  "#d4b87a", // warm sand
  "#7ab8c9", // teal
  "#a88da0", // plum
];

export const ExpenseForm: React.FC<ExpenseFormProps> = ({
  onSubmit,
  onCancel,
  onDelete,
  onRevise,
  canRevise = false,
  initialData,
  isLoading = false,
  isEditing = false,
  startupStartDate,
}) => {
  // Debug logging for initialData
  console.log("ExpenseForm received initialData:", initialData);
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateExpenseData>({
    defaultValues: {
      name: initialData?.name || "",
      category: initialData?.category || "Operations",
      monthlyAmount: initialData?.monthlyAmount || 0,
      startDate:
        toDateInputValue(initialData?.startDate) ||
        toDateInputValue(new Date()),
      endDate: toDateInputValue(initialData?.endDate),
      dayOfMonth: initialData?.dayOfMonth || 1,
      color: initialData?.color || EXPENSE_COLORS[0],
      // New scaling fields - convert from decimal to percentage for display
      expenseType: initialData?.expenseType || "fixed",
      growthRate: initialData?.growthRate ? initialData.growthRate * 100 : 0,
      revenueRatio: initialData?.revenueRatio
        ? initialData.revenueRatio * 100
        : 0,
      customerBaseCostPerCustomer: initialData?.customerBaseCostPerCustomer || 0,
      customerBaseAmount: initialData?.customerBaseAmount || 0,
      isOneTime: initialData?.isOneTime || false,
      oneTimeAmount: initialData?.oneTimeAmount || 0,
    },
  });

  const selectedCategory = watch("category");
  const expenseType = watch("expenseType");
  const isOneTime = watch("isOneTime");

  // Auto-assign color based on category
  React.useEffect(() => {
    if (selectedCategory && !initialData?.color) {
      const categoryIndex = EXPENSE_CATEGORIES.indexOf(selectedCategory);
      const colorIndex =
        categoryIndex >= 0 ? categoryIndex % EXPENSE_COLORS.length : 0;
      setValue("color", EXPENSE_COLORS[colorIndex]);
    }
  }, [selectedCategory, initialData, setValue]);

  const handleFormSubmit = (data: CreateExpenseData) => {
    // Convert empty endDate to undefined and ensure numeric fields are numbers
    const processedData = {
      ...data,
      // Set monthlyAmount based on expense type
      monthlyAmount: data.isOneTime 
        ? undefined 
        : (data.expenseType === "fixed" || data.expenseType === "hybrid") 
          ? Number(data.monthlyAmount || 0)
          : undefined,
      oneTimeAmount: data.isOneTime && data.oneTimeAmount
        ? Number(data.oneTimeAmount)
        : undefined,
      dayOfMonth: data.isOneTime ? undefined : Number(data.dayOfMonth),
      growthRate: data.growthRate !== undefined && data.growthRate !== null ? Number(data.growthRate) / 100 : undefined, // Convert percentage to decimal
      revenueRatio: data.revenueRatio !== undefined && data.revenueRatio !== null
        ? Number(data.revenueRatio) / 100
        : undefined, // Convert percentage to decimal
      customerBaseCostPerCustomer: data.customerBaseCostPerCustomer ? Number(data.customerBaseCostPerCustomer) : undefined,
      customerBaseAmount: data.customerBaseAmount ? Number(data.customerBaseAmount) : undefined,
      endDate: data.endDate || undefined,
      isOneTime: data.isOneTime,
    };
    console.log('ExpenseForm: Form data before processing:', data);
    console.log('ExpenseForm: Processed data being submitted:', processedData);
    onSubmit(processedData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <FormField label="Expense Name" error={errors.name?.message}>
        <Input
          {...register("name", { required: "Expense name is required" })}
          placeholder="e.g., Developer Salary, Office Rent"
        />
      </FormField>

      <FormField label="Category" error={errors.category?.message}>
        <select
          {...register("category", { required: "Category is required" })}
          className="sh-select"
        >
          {EXPENSE_CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </FormField>

      {/* One-time vs Recurring selection */}
      <FormField label="Expense Frequency" error={errors.isOneTime?.message}>
        <div className="space-y-3">
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              value="false"
              checked={!isOneTime}
              onChange={() => setValue("isOneTime", false)}
              className="mr-2"
            />
            <span className="text-white/75">Recurring Monthly Expense</span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              value="true"
              checked={isOneTime}
              onChange={() => setValue("isOneTime", true)}
              className="mr-2"
            />
            <span className="text-white/75">One-Time Expense</span>
          </label>
        </div>
      </FormField>

      {/* Monthly Amount Field - only show for fixed and hybrid recurring expenses */}
      {!isOneTime && (expenseType === "fixed" || expenseType === "hybrid") && (
        <FormField
          label="Monthly Amount ($)"
          error={errors.monthlyAmount?.message}
        >
          <Input
            type="number"
            step="0.01"
            {...register("monthlyAmount", {
              required: "Monthly amount is required",
              min: { value: 0, message: "Amount must be positive" },
              valueAsNumber: true,
            })}
            placeholder="0.00"
          />
        </FormField>
      )}

      {/* One-Time Amount Field - only show for one-time expenses */}
      {isOneTime && (
        <FormField
          label="One-Time Amount ($)"
          error={errors.oneTimeAmount?.message}
        >
          <Input
            type="number"
            step="0.01"
            {...register("oneTimeAmount", {
              required: isOneTime ? "One-time amount is required" : false,
              min: { value: 0, message: "Amount must be positive" },
              valueAsNumber: true,
            })}
            placeholder="0.00"
          />
        </FormField>
      )}

      {/* Expense type selection - only show for recurring expenses */}
      {!isOneTime && (
        <FormField label="Expense Type" error={errors.expenseType?.message}>
          <select
            {...register("expenseType", { required: "Expense type is required" })}
            className="sh-select"
          >
            <option value="fixed">Fixed Amount Only</option>
            <option value="percentage">Percentage of Total Revenue Only</option>
            <option value="hybrid">Fixed Amount + Percentage of Revenue</option>
            <option value="customer_based">Cost Per Customer (e.g., Cloud Service Fees)</option>
          </select>
        </FormField>
      )}

      {/* Growth Rate Field - shown for recurring expenses (all types can have growth) */}
      {!isOneTime && (
        <FormField
          label="Monthly Growth Rate (%)"
          error={errors.growthRate?.message}
          helperText={
            expenseType === "customer_based" 
              ? "Applied to both cost per customer and base fixed amount"
              : expenseType === "percentage"
              ? "Applied to the calculated percentage of revenue"
              : "Applied to the monthly amount"
          }
        >
          <Input
            type="number"
            step="0.01"
            {...register("growthRate", {
              min: {
                value: -100,
                message: "Growth rate cannot be less than -100%",
              },
              valueAsNumber: true,
            })}
            placeholder="0.00"
          />
        </FormField>
      )}

      {/* Revenue Ratio Field - shown only for percentage and hybrid types (recurring only) */}
      {!isOneTime && (expenseType === "percentage" || expenseType === "hybrid") && (
        <FormField
          label="Percentage of Revenue (%)"
          error={errors.revenueRatio?.message}
        >
          <Input
            type="number"
            step="0.01"
            {...register("revenueRatio", {
              required:
                expenseType === "percentage"
                  ? "Revenue percentage is required"
                  : false,
              min: { value: 0, message: "Percentage must be positive" },
              max: { value: 100, message: "Percentage cannot exceed 100%" },
              valueAsNumber: true,
            })}
            placeholder="0.00"
          />
        </FormField>
      )}

      {/* Customer-based expense fields - shown only for customer_based type (recurring only) */}
      {!isOneTime && expenseType === "customer_based" && (
        <>
          <FormField
            label="Cost Per Customer ($)"
            error={errors.customerBaseCostPerCustomer?.message}
          >
            <Input
              type="number"
              step="0.01"
              {...register("customerBaseCostPerCustomer", {
                required: "Cost per customer is required",
                min: { value: 0, message: "Cost must be positive" },
                valueAsNumber: true,
              })}
              placeholder="2.50"
            />
          </FormField>

          <FormField
            label="Base Fixed Amount ($) (Optional)"
            error={errors.customerBaseAmount?.message}
          >
            <Input
              type="number"
              step="0.01"
              {...register("customerBaseAmount", {
                min: { value: 0, message: "Amount must be positive" },
                valueAsNumber: true,
              })}
              placeholder="0.00"
            />
            <small className="text-white/65 text-sm mt-1 block">
              Optional minimum cost regardless of customer count
            </small>
          </FormField>
        </>
      )}

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Start Date" error={errors.startDate?.message}>
          <DateInputWithShortcut
            shortcutDate={startupStartDate}
            {...register("startDate", { required: "Start date is required" })}
            onUseShortcut={(value) =>
              setValue("startDate", value, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          />
        </FormField>

        <FormField label="End Date (Optional)" error={errors.endDate?.message}>
          <Input type="date" {...register("endDate")} />
        </FormField>
      </div>

      {/* Day of Month - only for recurring expenses */}
      {!isOneTime && (
        <FormField label="Day of Month" error={errors.dayOfMonth?.message}>
          <Input
            type="number"
            min="1"
            max="31"
            {...register("dayOfMonth", {
              min: { value: 1, message: "Must be between 1-31" },
              max: { value: 31, message: "Must be between 1-31" },
              valueAsNumber: true,
            })}
            placeholder="1"
          />
        </FormField>
      )}

      <FormField label="Color" error={errors.color?.message}>
        <div className="flex items-center gap-2">
          <input
            type="color"
            {...register("color")}
            className="w-12 h-10 border border-white/10 rounded cursor-pointer"
          />
          <Input
            {...register("color")}
            placeholder="#b893a6"
            className="flex-1"
          />
        </div>
      </FormField>

      {isEditing && canRevise && onRevise && !watch("isOneTime") ? (
        <FormHelpCallout>
          Changing amount or growth here rewrites history. To keep past months as
          modeled,{" "}
          <button
            type="button"
            className="font-medium text-sky-300 underline underline-offset-2"
            onClick={onRevise}
          >
            revise from a date
          </button>{" "}
          instead.
        </FormHelpCallout>
      ) : null}

      <div className="flex gap-3 pt-4">
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={isLoading}
          className="flex-1"
        >
          {isLoading ? "Saving..." : "Save"}
        </Button>
        {isEditing && onDelete && (
          <Button
            type="button"
            variant="danger"
            size="md"
            onClick={onDelete}
            disabled={isLoading}
          >
            Delete
          </Button>
        )}
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};
